<template>
   <div id="auth-login-code-input" class="mb-2 text-sm">
      <div class="mb-4 flex justify-center">
         <div v-for="(inputLabel, key) in state.inputs" :key="key">
            <label class="sr-only" :for="key">{{ inputLabel }}</label>
            <input
               :id="key"
               ref="$inputRefs"
               v-model="state.inputs[key]"
               inputmode="numeric"
               type="text"
               :data-cy="`code-${slugify(key)}`"
               maxlength="1"
               class="border bg-grey-10 text-black"
               @paste.prevent="onPaste"
               @input="handleInput(key)"
            />
         </div>
      </div>

      <div v-if="$v.$error && showValidation" class="text-lighter mt-3 text-sm leading-none text-error-red">
         <span
            v-if="$v.loginCode.mustBeSixDigits.$invalid || $v.loginCode.numeric.$invalid"
            data-cy="login-code-val-must-be-digits"
         >
            {{ $t("validation.login_code.invalid_input") }}
         </span>
         <span v-else-if="$v.loginCode.required.$invalid" data-cy="login-code-val-required">{{
            $t("validation.login_code.required")
         }}</span>
      </div>
   </div>
</template>
<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { numeric, required, helpers } from "@vuelidate/validators";
import { slugify } from "@/js/helpers/slugify";

const $emit = defineEmits(["login-code-entered", "login-code-invalidated"]);

// refs
// -----------------------------------------
const $inputRefs = ref([]);

// props
// -----------------------------------------
defineProps({
   showValidation: {
      type: Boolean,
      default: false,
   },
});

// data
// -----------------------------------------
const state = reactive({
   // inputLabels: i18n-vue/locales/validation/login_code,
   // inputText,
   // loginCode: "",
   inputs: {
      input_one: "",
      input_two: "",
      input_three: "",
      input_four: "",
      input_five: "",
      input_six: "",
   },
});

// computed
// -----------------------------------------
const loginCode = computed(() => {
   $v.value.$touch();

   return (
      state.inputs.input_one +
      state.inputs.input_two +
      state.inputs.input_three +
      state.inputs.input_four +
      state.inputs.input_five +
      state.inputs.input_six
   );
});

const rules = {
   loginCode: {
      mustBeSixDigits: (val) => {
         return !helpers.req(val) || val.length === 6;
      },
      numeric: numeric,
      required: required,
   },
};
const $v: any = useVuelidate(rules, { loginCode });

// mounted
// -----------------------------------------
onMounted(() => {
   $v.value.$touch();
});

// methods
// -----------------------------------------
const emitInput = () => {
   if ($v.value.$invalid) {
      // if input is not value (deleted something) tell parent to reset value
      $v.value.$touch();
      $emit("login-code-invalidated");
      return;
   } else {
      // If the input is valid, we'll pass it up for handling
      $emit("login-code-entered", loginCode.value);
   }
};

const onPaste = (val) => {
   clearInputs(); // clear previous
   const pastedAsText = val.clipboardData.getData("text").trim();
   // Split string to array of characters
   const chars = [...pastedAsText];
   //  Populate inputs.
   populateInputs(chars);
   // focus last input
   $inputRefs.value[$inputRefs.value.length - 1].focus();
   $v.value.$touch();

   emitInput();
};

const handleInput = (key) => {
   focusNext(key);
   emitInput();
};

const focusNext = (key) => {
   let target;
   const valueFromInput = state.inputs[key];
   if (valueFromInput === "") return; // don't auto focus if an input had content deleted

   switch (key) {
      case "input_one":
         target = "input_two";
         break;
      case "input_two":
         target = "input_three";
         break;
      case "input_three":
         target = "input_four";
         break;
      case "input_four":
         target = "input_five";
         break;
      case "input_five":
         target = "input_six";
         break;
      // when we reach input six, we don't want to focus on anything
      default:
         return;
   }

   $inputRefs.value.find((inputRef) => inputRef.id === target).focus();
};

const populateInputs = (chars) => {
   state.inputs.input_one = chars[0] ? chars[0].trim() : "";
   state.inputs.input_two = chars[1] ? chars[1].trim() : "";
   state.inputs.input_three = chars[2] ? chars[2].trim() : "";
   state.inputs.input_four = chars[3] ? chars[3].trim() : "";
   state.inputs.input_five = chars[4] ? chars[4].trim() : "";
   state.inputs.input_six = chars[5] ? chars[5].trim() : "";
   $v.value.$touch();
};

const clearInputs = () => {
   state.inputs.input_one = "";
   state.inputs.input_two = "";
   state.inputs.input_three = "";
   state.inputs.input_four = "";
   state.inputs.input_five = "";
   state.inputs.input_six = "";
   $v.value.$touch();
};
</script>

<style lang="css" scoped>
input {
   @apply m-1 w-10 h-10 text-lg focus:border focus:border-black rounded-full capitalize text-black  text-center;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
   -webkit-appearance: none;
   margin: 0;
}

/* Firefox */
input[type="number"] {
   -moz-appearance: textfield;
}
</style>
